import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { Loader2 } from 'lucide-react';
import { Modal } from '@qh/shared';
import { ApiError, candidateApi } from '../../lib/apiClient';
import { DeclarationFields, type DeclarationAnswer } from './DeclarationFields';

/**
 * Recruitment workflow §6.4 (step 5e) — the declaration popup on apply.
 *
 * Apply refuses with `DECLARATION_REQUIRED` when the candidate has never given the fraud
 * declaration (grandfathered website candidates, and accounts reached through forgot-password or
 * Google linking — step 5f). The apply caller then opens this popup; saving goes through the
 * existing `PATCH /candidates/me/profile` (consents only — nothing else on the profile changes)
 * and calls `onDone`, which retries the apply once.
 *
 * Same wording and rules as the profile page (shared `DeclarationFields`): RTR optional Yes / No,
 * fraud must be Yes. RTR is prefilled from the saved profile so an earlier answer doesn't look
 * reset; if that read fails it starts blank, and an unanswered RTR is not sent, so the stored
 * answer is kept either way.
 *
 * Mounted in `App.tsx` next to `ProfileCompletionGate`, which it mirrors.
 */

export interface DeclarationRequest {
  jobTitle: string;
  /** Called after the declarations are saved — the caller retries the apply. */
  onDone: () => void;
  /** Called when the candidate closes the popup without saving. */
  onCancel?: () => void;
}

interface DeclarationGateApi {
  requireDeclarations: (request: DeclarationRequest) => void;
}

const DeclarationGateContext = createContext<DeclarationGateApi | null>(null);

export function useDeclarationGate(): DeclarationGateApi {
  const ctx = useContext(DeclarationGateContext);
  if (!ctx) throw new Error('useDeclarationGate must be used within <DeclarationGate>');
  return ctx;
}

export function DeclarationGate({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<DeclarationRequest | null>(null);

  const requireDeclarations = useCallback((r: DeclarationRequest) => setRequest(r), []);

  const close = useCallback(
    (saved: boolean) => {
      const pending = request;
      setRequest(null);
      if (!pending) return;
      // Let the close render before the caller's follow-up (retry or toast) runs.
      setTimeout(() => (saved ? pending.onDone() : pending.onCancel?.()), 0);
    },
    [request],
  );

  const api = useMemo<DeclarationGateApi>(() => ({ requireDeclarations }), [requireDeclarations]);

  return (
    <DeclarationGateContext.Provider value={api}>
      {children}
      <DeclarationModal request={request} onClose={() => close(false)} onSaved={() => close(true)} />
    </DeclarationGateContext.Provider>
  );
}

function DeclarationModal({
  request,
  onClose,
  onSaved,
}: {
  request: DeclarationRequest | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const open = request !== null;
  const [rtr, setRtr] = useState<DeclarationAnswer>('');
  const [fraud, setFraud] = useState<DeclarationAnswer>('');
  const [prefilling, setPrefilling] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fresh form on every open; RTR prefilled from the saved profile (blank if the read fails).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setRtr('');
    setFraud('');
    setError(null);
    setSaving(false);
    setPrefilling(true);
    candidateApi.profile
      .get()
      .then((p) => {
        if (cancelled) return;
        setRtr(p.rtr_consent === true ? 'yes' : p.rtr_consent === false ? 'no' : '');
      })
      .catch(() => {
        // Prefill is a convenience — start blank.
      })
      .finally(() => {
        if (!cancelled) setPrefilling(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  async function handleSave() {
    if (fraud !== 'yes' || saving) return;
    setSaving(true);
    setError(null);
    try {
      await candidateApi.profile.update({
        fraud_declaration_consent: true,
        ...(rtr === '' ? {} : { rtr_consent: rtr === 'yes' }),
      });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save your declarations. Please try again.');
      setSaving(false);
    }
  }

  const fraudError = fraud === 'no' ? 'You need to agree to the Fraud Declaration to apply.' : null;

  return (
    <Modal
      open={open}
      onClose={() => {
        if (!saving) onClose();
      }}
      dismissible={!saving}
      title="One more step before you apply"
      description={
        request
          ? `Please confirm the declarations below to apply for ${request.jobTitle}. You only need to do this once.`
          : undefined
      }
      size="lg"
      footer={
        <div className="flex flex-wrap items-center justify-end gap-2 w-full">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex items-center rounded-2xl border border-gray-200 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-brand-muted hover:text-brand-primary hover:border-brand-primary disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={fraud !== 'yes' || saving}
            className="inline-flex items-center gap-1.5 rounded-2xl bg-brand-primary px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-white hover:bg-brand-deep disabled:opacity-50 transition-colors"
          >
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {saving ? 'Saving…' : 'Save and apply'}
          </button>
        </div>
      }
    >
      {prefilling ? (
        <div className="flex items-center gap-2 text-sm text-brand-muted">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading…
        </div>
      ) : (
        <div className="space-y-4">
          <DeclarationFields
            rtr={rtr}
            fraud={fraud}
            onRtrChange={setRtr}
            onFraudChange={setFraud}
            fraudError={fraudError}
            namePrefix="apply_"
            disabled={saving}
          />
          {error && <p className="text-sm text-red-700 font-medium">{error}</p>}
        </div>
      )}
    </Modal>
  );
}
